/* Meeting suggestions addressed to ME (§11): pieces of knowledge I dropped in
   a recorded meeting and tasks I agreed to, mined by the meeting pipeline and
   waiting for my approve/dismiss.

   Server-backed (the memory layer's DB is the source of truth — no account
   data): boot fetches my pending set, and the app.atrium.readout event every
   participant receives in the meeting room is the live "new batch" trigger.
   Approving a task creates the real ticket with MY OWN token (attribution and
   authz for free), then records the resolution; approving knowledge queues an
   ordinary memory dump server-side (dedup/diff/rollback all apply). */
import { defineStore } from 'pinia'
import { sync } from '@/lib/sync'
import { matrix } from '@/lib/matrix'
import { atriumApi } from '@/lib/atriumApi'
import {
  memoryApi, type Suggestion, type KnowledgeSuggestionPayload, type TaskSuggestionPayload,
} from '@/lib/memoryApi'
import { useFloor } from '@/stores/floor'
import { useTasks } from '@/stores/tasks'
import { usePresence } from '@/stores/presence'
import { useToasts } from '@/stores/toasts'
import { playSfx } from '@/lib/sfx'
import { localpart } from '@/lib/util'

export const useSuggestions = defineStore('suggestions', {
  state: () => ({
    items: [] as Suggestion[],
    loaded: false,
    inited: false,
    busy: {} as Record<number, boolean>,
    refreshTimer: null as ReturnType<typeof setTimeout> | null,
  }),

  getters: {
    pending: (s): Suggestion[] => s.items.filter((x) => x.status === 'pending'),
    pendingCount(): number {
      return this.pending.length
    },
    knowledge(): Suggestion[] {
      return this.pending.filter((x) => x.kind === 'knowledge')
    },
    tasks(): Suggestion[] {
      return this.pending.filter((x) => x.kind === 'task')
    },
  },

  actions: {
    init() {
      if (this.inited) return
      this.inited = true
      // A meeting readout landing in a room I participated in means fresh
      // suggestions may exist for me — refresh (debounced) and toast the delta.
      sync.on('custom', (_roomId, ev) => {
        if (ev.type !== 'app.atrium.readout') return
        if ((ev.content as any)?.kind !== 'meeting_summary') return
        this.scheduleRefresh()
      })
    },

    /** Boot: load my pending set; one quiet summary toast if any await me. */
    async boot() {
      this.init()
      if (this.loaded) return
      await this.refresh(false)
      this.loaded = true
      if (this.pendingCount && !usePresence().isDnd) this.toastPending(this.pendingCount, false)
    },

    scheduleRefresh() {
      if (this.refreshTimer) return
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null
        void this.refresh(true)
      }, 2000)
    },

    async refresh(live: boolean) {
      try {
        const before = new Set(this.items.filter((x) => x.status === 'pending').map((x) => x.id))
        const { suggestions } = await memoryApi.suggestions('pending')
        // Keep resolved items we already knew about (nice for in-view state),
        // replace the pending set wholesale.
        const resolved = this.items.filter((x) => x.status !== 'pending')
        this.items = [...suggestions, ...resolved]
        if (live) {
          const fresh = suggestions.filter((x) => !before.has(x.id)).length
          if (fresh && !usePresence().isDnd) {
            playSfx('mention')
            this.toastPending(fresh, true)
          }
        }
      } catch {
        /* memory layer down — keep what we have */
      }
    },

    toastPending(n: number, fresh: boolean) {
      useToasts().push({
        icon: 'spark',
        color: 'var(--gold-bright)',
        title: fresh
          ? `Your meeting produced ${n} suggestion${n > 1 ? 's' : ''}`
          : `${n} meeting suggestion${n > 1 ? 's' : ''} await${n > 1 ? '' : 's'} you`,
        text: 'Knowledge and tasks to approve or dismiss.',
        action: 'Review',
        ms: 7000,
        onAction: () => window.location.assign('#/mentions?tab=suggestions'),
      })
    },

    /** The board the picker PRE-SELECTS for a task suggestion: the meeting
        room's team board when the room is a team room, else the floor board.
        A default only — the person chooses the board when approving. */
    boardFor(s: Suggestion): string {
      const floor = useFloor()
      const tasks = useTasks()
      const room = s.room_id ? floor.rooms.find((r) => r.roomId === s.room_id) : undefined
      if (room?.team) {
        const board = tasks.boards.find((b) => b.kind === 'team' && b.team === room.team && b.default)
          ?? tasks.boards.find((b) => b.kind === 'team' && b.team === room.team)
        if (board) return board.id
      }
      return 'floor'
    },

    /** Approve one suggestion. Tasks carry the board the approver picked on
        the card (boardFor is only the picker's default — never decided here).
        `edits` is the card's edited title/body when the approver reworked the
        proposal — the edited version is what gets created/remembered. */
    async approve(s: Suggestion, boardId?: string, edits?: { title: string; body: string }): Promise<string | null> {
      if (this.busy[s.id]) return null
      this.busy[s.id] = true
      try {
        if (s.kind === 'task') {
          const p = s.payload as TaskSuggestionPayload
          const title = edits?.title.trim() || p.title
          const bodyMd = edits ? edits.body : p.description_md
          const tasks = useTasks()
          if (!tasks.boardsLoaded) await tasks.loadBoards()
          const board = boardId && tasks.boards.some((b) => b.id === boardId) ? boardId : this.boardFor(s)
          const me = matrix.userId()
          const when = s.meeting_started_at ? new Date(s.meeting_started_at).toLocaleDateString() : ''
          const agreed = (p.other_parties ?? [])
            .map((mxid) => `@[${useFloor().people[mxid]?.short || localpart(mxid)}](mxid:${mxid})`)
            .join(', ')
          const description = [
            bodyMd,
            '',
            `— From the meeting “${s.room_name || 'a recorded call'}”${when ? ` (${when})` : ''}${agreed ? ` · agreed with ${agreed}` : ''}${p.due_hint ? ` · due: ${p.due_hint}` : ''}`,
          ].join('\n')
          const ticket = await atriumApi.createTicket(board, {
            title,
            description,
            assignees: me && !p.unassigned ? [me] : [],
          })
          await memoryApi.approveSuggestion(s.id, { board, ticket: ticket.id })
          this.settle(s.id, 'approved', { board, ticket: ticket.id }, edits)
          useToasts().push({
            icon: 'kanban', color: 'var(--gold-bright)', title: 'Task created',
            text: `“${title}”`, action: 'Open', ms: 6000,
            onAction: () => window.location.assign(`#/tasks/${board}/${ticket.id}`),
          })
        } else {
          const p = s.payload as KnowledgeSuggestionPayload
          const title = edits?.title.trim() || p.title
          const res = await memoryApi.approveSuggestion(
            s.id,
            undefined,
            edits ? { title, content_md: edits.body } : undefined,
          )
          this.settle(s.id, 'approved', res.suggestion.resolution ?? undefined, edits)
          useToasts().push({
            icon: 'memory', color: 'var(--gold-bright)', title: 'Weaving into memory…',
            text: `“${title}” is being integrated into the knowledge graph.`,
            action: 'View', ms: 6000,
            onAction: () => window.location.assign('#/memory'),
          })
        }
        return null
      } catch (e) {
        void this.refresh(false) // re-sync truth after a failure mid-flow
        return e instanceof Error ? e.message : 'Could not approve the suggestion'
      } finally {
        this.busy[s.id] = false
      }
    },

    async dismiss(s: Suggestion, reason?: string): Promise<string | null> {
      if (this.busy[s.id]) return null
      this.busy[s.id] = true
      try {
        await memoryApi.dismissSuggestion(s.id, reason)
        this.settle(s.id, 'dismissed', reason ? { reason } : undefined)
        return null
      } catch (e) {
        void this.refresh(false)
        return e instanceof Error ? e.message : 'Could not dismiss the suggestion'
      } finally {
        this.busy[s.id] = false
      }
    },

    settle(id: number, status: Suggestion['status'], resolution?: Suggestion['resolution'], edits?: { title: string; body: string }) {
      const it = this.items.find((x) => x.id === id)
      if (it) {
        it.status = status
        if (resolution !== undefined) it.resolution = resolution
        it.resolved_at = new Date().toISOString()
        if (edits) {
          // The resolved card shows what was actually approved, not the original proposal.
          if (edits.title.trim()) it.payload.title = edits.title.trim()
          if (it.kind === 'task') (it.payload as TaskSuggestionPayload).description_md = edits.body
          else (it.payload as KnowledgeSuggestionPayload).content_md = edits.body
        }
      }
    },
  },
})
